import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Globe, Lock, Save, Undo2 } from "lucide-react";
import { toast } from "sonner";
import {
  MARKET_SETTINGS,
  SETTING_GROUP_LABEL,
  SETTING_GROUPS,
  type SettingDef,
  type SettingValue,
} from "@poji/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input, Label, NativeSelect } from "@/components/ui/input";
import { ActionDialog } from "@/components/action-dialog";
import { ApiError, api } from "@/lib/api";
import { ErrorState, LoadingRows, PageHeader, Spinner, errorText } from "@/components/common";
import { useAuth } from "@/lib/auth";
import { dateTime } from "@/lib/format";
import { parseEuroToMinor } from "@/lib/money-input";
import { cn } from "@/lib/utils";

interface Market {
  id: string;
  code: string;
  name: string;
  currency: string;
  locale: string;
  timezone: string;
  isActive: boolean;
  updatedAt: string | null;
  settings: Record<string, SettingValue> | null;
}

const UNIT_SUFFIX: Partial<Record<SettingDef["unit"], string>> = {
  minutes: "dk",
  hours: "saat",
  hours_decimal: "saat",
  days: "gün",
  bps: "%",
  money: "€",
  meters: "m",
  minutes_list: "dk",
  rating: "/ 5",
};
const ISSUE_TEXT: Record<string, string> = {
  range: "izin verilen aralığın dışında",
  integer: "tam sayı olmalı",
  number: "sayı olmalı",
  quarter_hours: "çeyrek saat adımlarıyla (0,25)",
  ascending: "küçükten büyüğe, tekrarsız",
  list: "virgülle ayrılmış tam sayılar",
  money: "geçerli bir tutar değil",
  time: "SS:DD biçiminde",
  required: "boş bırakılamaz",
  read_only: "panelden değiştirilemez",
};
const RULE_TEXT: Record<string, string> = {
  trusted_payout_after_regular: "Güvenilir hizmet verene ödeme, yeni hizmet verenden geç olamaz.",
  min_billable_over_provider_max:
    "En az faturalanan süre, hizmet verenin seçebileceği en yüksek minimumdan büyük olamaz.",
  increment_over_min_billable: "Faturalama adımı, en az faturalanan süreden büyük olamaz.",
};

/** API değeri → kutudaki metin */
function toText(def: SettingDef, v: SettingValue): string {
  if (v === null || v === undefined) return "";
  switch (def.unit) {
    case "bps":
      return String((v as number) / 100).replace(".", ",");
    case "money":
      return (Number(v) / 100).toFixed(2).replace(".", ",").replace(/,00$/, "");
    case "hours_decimal":
    case "rating":
      return String(v).replace(".", ",");
    case "minutes_list":
      return (v as number[]).join(", ");
    case "boolean":
      return v ? "true" : "false";
    default:
      return String(v);
  }
}

/** Kutudaki metin → API değeri; çevrilemezse "invalid" */
function fromText(def: SettingDef, t: string): SettingValue | "invalid" {
  const s = t.trim();
  if (s === "") return def.nullable ? null : "invalid";
  const num = (x: string) => (/^-?\d+([.,]\d+)?$/.test(x) ? Number(x.replace(",", ".")) : NaN);
  switch (def.unit) {
    case "boolean":
      return s === "true";
    case "time":
      return s.slice(0, 5);
    case "money":
      return parseEuroToMinor(s) ?? "invalid";
    case "bps": {
      const n = num(s);
      return Number.isNaN(n) ? "invalid" : Math.round(n * 100);
    }
    case "minutes_list": {
      const parts = s
        .split(/[,\s]+/)
        .filter(Boolean)
        .map(Number);
      return parts.some((n) => !Number.isInteger(n)) ? "invalid" : parts;
    }
    default: {
      const n = num(s);
      return Number.isNaN(n) ? "invalid" : n;
    }
  }
}

function rangeText(def: SettingDef) {
  if (def.min === undefined || def.unit === "boolean" || def.unit === "time") return null;
  const f = (n: number) =>
    def.unit === "bps" ? `%${n / 100}` : def.unit === "money" ? `€${n / 100}` : String(n);
  return `${f(def.min)}–${f(def.max!)}`;
}

function Field({
  def,
  value,
  dirty,
  issue,
  disabled,
  lockedReason,
  onChange,
}: {
  def: SettingDef;
  value: string;
  dirty: boolean;
  issue: string | undefined;
  disabled: boolean;
  lockedReason: string | null;
  onChange: (v: string) => void;
}) {
  const id = `s-${def.key}`;
  const range = rangeText(def);
  return (
    <div className={cn("grid content-start gap-1.5", !def.live && "opacity-70")}>
      <div className="flex flex-wrap items-center gap-1.5">
        <Label htmlFor={id}>{def.label}</Label>
        {def.decision && (
          <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
            {def.decision}
          </Badge>
        )}
        {!def.live && (
          <Badge
            variant="secondary"
            className="px-1.5 py-0 text-[10px]"
            title="Kod henüz kullanmıyor"
          >
            yakında
          </Badge>
        )}
        {lockedReason && (
          <Lock className="size-3 text-muted-foreground" aria-label={lockedReason} />
        )}
      </div>
      {def.unit === "boolean" ? (
        <NativeSelect
          id={id}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className={cn("w-40", dirty && "ring-2 ring-primary/40")}
        >
          <option value="true">Açık</option>
          <option value="false">Kapalı</option>
        </NativeSelect>
      ) : (
        <div className="flex items-center gap-2">
          <Input
            id={id}
            type={def.unit === "time" ? "time" : "text"}
            inputMode={def.unit === "time" ? undefined : "decimal"}
            value={value}
            disabled={disabled}
            aria-invalid={Boolean(issue)}
            onChange={(e) => onChange(e.target.value)}
            className={cn(
              def.unit === "minutes_list" ? "w-56" : "w-32",
              dirty && "ring-2 ring-primary/40",
              issue && "border-destructive",
            )}
          />
          {UNIT_SUFFIX[def.unit] && (
            <span className="text-sm text-muted-foreground">{UNIT_SUFFIX[def.unit]}</span>
          )}
        </div>
      )}
      {issue ? (
        <p className="text-xs text-destructive">{issue}</p>
      ) : (
        <p className="text-xs text-muted-foreground">
          {[def.help, lockedReason, range && `aralık ${range}`].filter(Boolean).join(" · ")}
        </p>
      )}
    </div>
  );
}

function ActivateMarket({ market }: { market: Market }) {
  const qc = useQueryClient();
  const open = !market.isActive;
  return (
    <ActionDialog
      trigger={
        <Button size="sm" variant={open ? "default" : "outline"}>
          <Globe /> {open ? "Pazarı aç" : "Pazarı kapat"}
        </Button>
      }
      title={open ? `${market.name}: pazarı aç` : `${market.name}: pazarı kapat`}
      description={
        open
          ? "Müşteriler bu ülkedeki hizmetleri görür ve iş açabilir. Açık en az bir hizmet, ayarlar ve genel komisyon kuralı gerekir."
          : "Yeni iş açılamaz ve katalog müşteriye görünmez. Açık işler sürer."
      }
      confirmLabel={open ? "Pazarı aç" : "Pazarı kapat"}
      confirmVariant={open ? "default" : "destructive"}
      success={open ? "Pazar açıldı." : "Pazar kapatıldı."}
      onSubmit={async (key) => {
        await api(`/v1/admin/markets/${market.id}/${open ? "activate" : "deactivate"}`, {
          method: "POST",
          idempotencyKey: key,
          body: {},
        });
        await qc.invalidateQueries({ queryKey: ["admin-markets"] });
      }}
    />
  );
}

export function MarketSettingsPage() {
  const qc = useQueryClient();
  const { state } = useAuth();
  const isSuper = state.kind === "ready" && state.staff.isSuperAdmin;
  const q = useQuery({
    queryKey: ["admin-markets"],
    queryFn: () => api<{ markets: Market[] }>("/v1/admin/markets"),
  });
  const [marketId, setMarketId] = React.useState<string | null>(null);
  const market = q.data?.markets.find((m) => m.id === marketId) ?? q.data?.markets[0];
  const [draft, setDraft] = React.useState<Record<string, string>>({});
  const [issues, setIssues] = React.useState<Record<string, string>>({});
  const [busy, setBusy] = React.useState(false);

  const saved = React.useMemo(
    () =>
      Object.fromEntries(
        MARKET_SETTINGS.map((d) => [d.key, toText(d, market?.settings?.[d.key] ?? null)]),
      ),
    [market],
  );
  // pazar değişince ya da kayıt sonrası taslak sıfırlanır
  React.useEffect(() => {
    setDraft({});
    setIssues({});
  }, [market?.id, market?.updatedAt]);

  const changed = Object.keys(draft).filter((k) => draft[k] !== saved[k]);

  async function submit() {
    if (!market) return;
    const values: Record<string, SettingValue> = {};
    const local: Record<string, string> = {};
    for (const k of changed) {
      const def = MARKET_SETTINGS.find((d) => d.key === k)!;
      const v = fromText(def, draft[k]!);
      if (v === "invalid") local[k] = def.nullable ? ISSUE_TEXT.number! : ISSUE_TEXT.required!;
      else values[k] = v;
    }
    setIssues(local);
    if (Object.keys(local).length) return;
    setBusy(true);
    try {
      await api(`/v1/admin/markets/${market.id}/settings`, {
        method: "PATCH",
        body: { values, expectedUpdatedAt: market.updatedAt },
      });
      toast.success(`${changed.length} ayar kaydedildi. Yeni işlere uygulanır.`);
      await qc.invalidateQueries({ queryKey: ["admin-markets"] });
    } catch (e) {
      if (e instanceof ApiError && e.details?.issues) {
        const list = e.details.issues as { key: string; reason: string }[];
        setIssues(Object.fromEntries(list.map((i) => [i.key, ISSUE_TEXT[i.reason] ?? i.reason])));
        toast.error("Bazı değerler geçersiz.");
      } else if (e instanceof ApiError && e.details?.rules) {
        toast.error((e.details.rules as string[]).map((r) => RULE_TEXT[r] ?? r).join(" "));
      } else if (e instanceof ApiError && e.status === 409) {
        toast.error(
          "Bu arada başka biri ayarları değiştirdi. Sayfa yenilendi; değişikliklerinizi tekrar girin.",
        );
        await qc.invalidateQueries({ queryKey: ["admin-markets"] });
      } else toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  if (q.isPending) return <LoadingRows rows={8} />;
  if (q.error || !market) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;

  return (
    <>
      <PageHeader
        title="Pazar ayarları"
        description={
          <>
            Ülkeye özgü işletme kuralları. Değişiklik yeni işlere uygulanır; açık işler kendi
            kurallarını korur. Her değişiklik denetim kaydına yazılır.
            {market.updatedAt && <> Son değişiklik: {dateTime(market.updatedAt)}.</>}
          </>
        }
        actions={
          <>
            {q.data!.markets.length > 1 && (
              <NativeSelect
                aria-label="Pazar"
                value={market.id}
                onChange={(e) => setMarketId(e.target.value)}
              >
                {q.data!.markets.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </NativeSelect>
            )}
            <Badge variant={market.isActive ? "success" : "warning"}>
              {market.name}: {market.isActive ? "müşterilere açık" : "henüz kapalı"}
            </Badge>
            {isSuper && <ActivateMarket market={market} />}
          </>
        }
      />
      <p className="mb-4 text-sm text-muted-foreground">
        {market.currency} · {market.timezone} · varsayılan dil {market.locale}
      </p>

      {!market.settings ? (
        <ErrorState error={new Error("Bu pazarın ayar satırı yok (seed).")} />
      ) : (
        <div className="grid gap-4 pb-24">
          {SETTING_GROUPS.map((g) => (
            <Card key={g}>
              <CardHeader>
                <CardTitle className="text-base">{SETTING_GROUP_LABEL[g]}</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-x-8 gap-y-5 sm:grid-cols-2 xl:grid-cols-3">
                {MARKET_SETTINGS.filter((d) => d.group === g).map((d) => {
                  const locked = d.readOnly
                    ? "panelden değişmez"
                    : d.superAdminOnly && !isSuper
                      ? "yalnızca super admin"
                      : null;
                  const value = draft[d.key] ?? saved[d.key]!;
                  return (
                    <Field
                      key={d.key}
                      def={d}
                      value={value}
                      dirty={d.key in draft && draft[d.key] !== saved[d.key]}
                      issue={issues[d.key]}
                      disabled={Boolean(locked) || busy}
                      lockedReason={locked}
                      onChange={(v) => setDraft((x) => ({ ...x, [d.key]: v }))}
                    />
                  );
                })}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {changed.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t bg-card/95 backdrop-blur lg:left-60">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3">
            <span className="text-sm">
              <b>{changed.length}</b> ayar değişti
            </span>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => {
                  setDraft({});
                  setIssues({});
                }}
              >
                <Undo2 /> Vazgeç
              </Button>
              <Button size="sm" disabled={busy} onClick={() => void submit()}>
                {busy ? <Spinner /> : <Save />} Kaydet
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
